import { useEffect, useMemo, useState } from "react";
import RequestTransparency from "./RequestTransparency";
import {
  formatRemainingLabel,
  resolveRequestTransparency,
} from "../../utils/requestTransparency";
import "./HomeRecentRequests.css";

function StatusBadge({ request }) {
  return (
    <span className={`ess-status-badge ess-status-${request.status}`}>
      {request.statusLabel}
    </span>
  );
}

function RequestSummaryLine({ request }) {
  const [now, setNow] = useState(() => new Date());
  const source = request.source || request;
  const transparency = useMemo(
    () => resolveRequestTransparency(source, now),
    [source, now],
  );

  useEffect(() => {
    if (!transparency?.sla?.applies) return undefined;
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [transparency?.sla?.applies]);

  if (!transparency) return null;

  if (transparency.currentHolder) {
    const sla = transparency.sla;
    const slaText =
      sla?.applies
        ? sla.remainingMs <= 0
          ? " — حان موعد الرد"
          : ` — ${formatRemainingLabel(sla.remainingMs, sla.paused)}`
        : "";
    return (
      <p className="ess-home-req-summary">
        عند <strong>{transparency.currentHolder.roleName}</strong>
        {slaText}
      </p>
    );
  }

  if (transparency.rejection) {
    return (
      <p className="ess-home-req-summary is-rejected">
        {transparency.rejection.reason}
      </p>
    );
  }

  if (transparency.disbursement) {
    return (
      <p className="ess-home-req-summary">
        {transparency.disbursement.expectedLabel}
      </p>
    );
  }

  return null;
}

function HomeRequestCard({ request, onAppeal, onResubmit }) {
  const [expanded, setExpanded] = useState(false);
  const source = request.source || request;
  const canAppeal = request.status === "rejected";

  return (
    <article className={`ess-home-req-card${expanded ? " is-expanded" : ""}`}>
      <button
        type="button"
        className="ess-home-req-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="ess-home-req-card-top">
          <h3 className="ess-home-req-card-type">{request.type}</h3>
          <StatusBadge request={request} />
        </div>
        {request.date ? (
          <p className="ess-home-req-date">تاريخ التقديم: {request.date}</p>
        ) : null}
        {!expanded ? <RequestSummaryLine request={request} /> : null}
        <span className="ess-home-req-more">
          {expanded ? "إخفاء التفاصيل" : "عرض التفاصيل"}
        </span>
      </button>

      {expanded ? (
        <div className="ess-home-req-details">
          <RequestTransparency
            request={source}
            onResubmit={onResubmit}
          />
          {canAppeal ? (
            <button
              type="button"
              className="ess-appeal-btn"
              onClick={() => onAppeal(request.id)}
            >
              تقديم تظلم
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default function HomeRecentRequests({
  requests = [],
  onAppeal,
  onResubmit,
}) {
  return (
    <section className="ess-table-card ess-home-recent-section" dir="rtl">
      <div className="ess-home-section-head">
        <h2 className="ess-table-title">طلباتي</h2>
        <p className="ess-home-section-sub">
          المعلّق أولًا، ثم المرفوض، ثم آخر المعتمد
        </p>
      </div>
      {requests.length === 0 ? (
        <p className="ess-empty-message">لا توجد طلبات مسجلة</p>
      ) : (
        <div className="ess-home-req-list">
          {requests.map((request) => (
            <HomeRequestCard
              key={request.id}
              request={request}
              onAppeal={onAppeal}
              onResubmit={onResubmit}
            />
          ))}
        </div>
      )}
    </section>
  );
}
